import { Editor, Editor as TipTapEditor } from '@tiptap/core';
import { EditorProps } from '@tiptap/pm/view';
import {
  EditorContent,
  Extension,
  JSONContent,
  UseEditorOptions,
  useEditor,
} from '@tiptap/react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';

import { EditorBubbleMenu } from './editor/bubble-menu';
import { defaultExtensions } from './editor/extensions';
import { defaultEditorProps } from './props';
import { decideMarkdownSync } from './reconcileEditorContentSync';

export type EluveEditorProps = {
  /**
   * The editor content
   */
  content?: JSONContent | string;
  /**
   * A list of extensions to use for the editor, in addition to the default Novel extensions.
   * Defaults to [].
   */
  extensions?: Extension[];
  /**
   * Props to pass to the underlying Tiptap editor, in addition to the default Novel editor props.
   * Defaults to {}.
   */
  editorProps?: EditorProps;
  /**
   * A callback function that is called whenever the editor is updated.
   * Defaults to () => {}.
   */
  onUpdate: (editor?: TipTapEditor) => void | Promise<void>;
  className?: string;
  disabled?: boolean;
};

export type EluveEditorHandle = {
  setEditable: (editable: boolean) => void;
  getText: () => string;
  getMarkdown: () => string;
};

const fallbackExtensions: Extension[] = [];
const fallbackEditorProps: EditorProps = {};

export const EluveEditor = forwardRef<EluveEditorHandle, EluveEditorProps>(
  (
    {
      content,
      className,
      onUpdate,
      extensions = fallbackExtensions,
      editorProps = fallbackEditorProps,
      disabled = false,
    },
    ref,
  ) => {
    const [editable, setEditable] = useState<boolean>(!disabled);

    const editorExtensions = useMemo(() => {
      const uniqueExtensions = [...defaultExtensions, ...extensions].reduce(
        (map, ext) =>
          ext?.config?.name
            ? map.set(ext?.config?.name, ext as Extension)
            : map,
        new Map<string, Extension>(),
      );
      return Array.from(uniqueExtensions.values());
    }, [extensions]);

    const tiptapEditorProps = useMemo(() => {
      return { ...defaultEditorProps, ...editorProps };
    }, [editorProps]);

    const editorOnUpdate = useMemo(() => {
      return ({ editor }: { editor: Editor }) => onUpdate(editor);
    }, [onUpdate]);

    // The editor is created ONCE with the initial content, captured in a ref so
    // it stays referentially stable. Otherwise v3's useEditor re-applies
    // options.content during the autosave re-render loop and wipes in-progress
    // edits. Post-mount content changes are reconciled by the effect below.
    const initialContentRef = useRef(content ?? '');
    const initialContentType =
      typeof initialContentRef.current === 'string' ? 'markdown' : 'json';

    const options: Partial<UseEditorOptions> = useMemo(
      () => ({
        extensions: editorExtensions,
        editorProps: tiptapEditorProps,
        onUpdate: editorOnUpdate,
        content: initialContentRef.current,
        contentType: initialContentType,
        editable,
        // https://tiptap.dev/blog/release-notes/say-hello-to-tiptap-2-5-our-most-performant-editor-yet
        immediatelyRender: true,
        shouldRerenderOnTransaction: false,
      }),
      [
        initialContentType,
        editorExtensions,
        editorOnUpdate,
        tiptapEditorProps,
        editable,
      ],
    );

    const editor = useEditor(options, []);

    useImperativeHandle(ref, () => ({
      setEditable(editable: boolean) {
        setEditable(editable);
      },
      getText() {
        return editor?.getText() ?? '';
      },
      getMarkdown() {
        return getTiptapMarkdownContentFromEditor(editor);
      },
    }));

    // the editor isn't available right away, so we manage
    // the editable state in this component and set it on
    // the editor when the editor becomes available
    useEffect(() => {
      editor?.setEditable(editable);
    }, [editor, editable]);

    // Reconcile EXTERNAL content changes into the editor without clobbering the
    // user's in-progress edits. `baselineRef` holds the editor's own
    // serialization of the content it was last loaded with; comparing the live
    // serialization to that baseline (same serializer on both sides) reliably
    // tells whether the user has edited — unlike comparing to the stored string,
    // which v3's serializer may not byte-match. If the editor has diverged from
    // its baseline it has unsaved edits, and we must never overwrite them.
    const baselineRef = useRef<string | null>(null);
    useEffect(() => {
      if (!editor || editor.isDestroyed || content == null) return;

      if (typeof content === 'string') {
        const current = getTiptapMarkdownContentFromEditor(editor);
        const decision = decideMarkdownSync({
          current,
          baseline: baselineRef.current,
          incoming: content,
          isFocused: editor.isFocused,
        });
        switch (decision.type) {
          // Editor already reflects the incoming content (echo, or seeded with
          // it at mount) — just refresh the baseline.
          case 'markClean':
            baselineRef.current = current;
            return;
          // Editor is focused, or holds unsaved user edits — leave it be.
          case 'skip':
            return;
          // Editor doesn't reflect the incoming content and has no unsaved edits
          // (incl. the first sync of async-loaded content) — apply it and reset
          // the baseline to the post-ingest serialization.
          case 'apply':
            editor.commands.setContent(content, {
              emitUpdate: false,
              contentType: 'markdown',
            });
            baselineRef.current = getTiptapMarkdownContentFromEditor(editor);
            return;
        }
      } else if (!editor.isFocused) {
        const currentJson = JSON.stringify(editor.getJSON());
        if (currentJson !== JSON.stringify(content)) {
          editor.commands.setContent(content, {
            emitUpdate: false,
            contentType: 'json',
          });
        }
      }
    }, [editor, content]);

    return (
      <>
        <EditorContent editor={editor} className={className} />
        <EditorBubbleMenu editor={editor} />
      </>
    );
  },
);
